/**
 * @file Lead table component.
 * @description Renders a paginated, formatted table of leads with optional export action and empty state.
 * @author Sanjay Jangid (Jiggle frontend)
 * @email sanjay.jangid@dgtoohl.com
 * @date 2026-05-10
 * @version 0.0.0
 */
import React from 'react';
import { format, parseISO } from 'date-fns';
import { Download, Eye } from 'lucide-react';
import type { LeadTableProps } from '../types/LeadTable.types.ts';
import { Panel } from './ui';

const TH = 'px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider align-top';
const TD_NOWRAP = 'px-4 py-4 align-top whitespace-nowrap text-sm text-gray-900';
const TD_WRAP =
  'px-4 py-4 align-top whitespace-normal break-words [overflow-wrap:anywhere] [word-break:break-word] text-sm text-gray-900';

/** Column widths for table-fixed layout (prevents long address overlap). */
const TABLE_COLUMNS = [
  { label: 'S.No', thClass: `${TH} text-center w-14`, colClass: 'w-14', tdClass: `${TD_NOWRAP} text-center` },
  { label: 'Name', thClass: `${TH} w-36`, colClass: 'w-36', tdClass: TD_WRAP },
  { label: 'Phone', thClass: `${TH} w-28`, colClass: 'w-28', tdClass: TD_NOWRAP },
  { label: 'Email', thClass: `${TH} w-40`, colClass: 'w-40', tdClass: TD_WRAP },
  { label: 'Company Name', thClass: `${TH} w-36`, colClass: 'w-36', tdClass: TD_WRAP },
  { label: 'Address', thClass: `${TH} w-56`, colClass: 'w-56', tdClass: TD_WRAP },
  { label: 'Vehicle Brand Name', thClass: `${TH} w-44`, colClass: 'w-44', tdClass: TD_WRAP },
  { label: 'DND Status', thClass: `${TH} w-24`, colClass: 'w-24', tdClass: TD_NOWRAP },
  { label: 'Plan', thClass: `${TH} w-24`, colClass: 'w-24', tdClass: TD_WRAP },
  { label: 'Age', thClass: `${TH} w-16`, colClass: 'w-16', tdClass: TD_NOWRAP },
  { label: 'City', thClass: `${TH} w-28`, colClass: 'w-28', tdClass: TD_WRAP },
  { label: 'Income', thClass: `${TH} w-28`, colClass: 'w-28', tdClass: TD_NOWRAP },
  { label: 'Source', thClass: `${TH} w-28`, colClass: 'w-28', tdClass: TD_NOWRAP },
  { label: 'Campaign', thClass: `${TH} w-32`, colClass: 'w-32', tdClass: TD_WRAP },
  { label: 'Data Type', thClass: `${TH} w-24`, colClass: 'w-24', tdClass: TD_NOWRAP },
  { label: 'Upload Date', thClass: `${TH} w-32`, colClass: 'w-32', tdClass: TD_NOWRAP },
] as const;

const LeadTable: React.FC<LeadTableProps> = ({
  leads,
  showActions = false,
  onExport,
  serialOffset = 0,
  embedded = false,
}) => {
  const formatCurrency = (amount: number | null) => {
    if (amount === null || isNaN(amount)) {
      return 'N/A';
    }
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) {
      return 'N/A';
    }
    try {
      const normalized = dateString.includes('T') ? dateString : dateString.replace(' ', 'T');
      return format(parseISO(normalized), 'MMM dd, yyyy');
    } catch {
      return 'Invalid Date';
    }
  };

  const displayValue = (value: string | null | undefined) => {
    const text = value == null ? '' : String(value).trim();
    if (!text || text.toLowerCase() === 'na' || text.toLowerCase() === 'n/a') {
      return 'N/A';
    }
    return text;
  };

  const tableBody = (
    <>
      {showActions && (
        <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold text-gray-900">
              Leads ({leads.length})
            </h3>
            <button
              onClick={onExport}
              className="px-4 py-2 text-sm font-medium text-white bg-gradient-to-r from-accent-500 to-accent-600 hover:from-accent-600 hover:to-accent-700 rounded-md transition-all transform hover:scale-105"
            >
              <Download size={16} className="inline mr-2" />
              Export CSV
            </button>
          </div>
        </div>
      )}
      
      <div className="overflow-x-auto">
        <table className="min-w-[1280px] w-full table-fixed divide-y divide-gray-200">
          <colgroup>
            {TABLE_COLUMNS.map((col) => (
              <col key={col.label} className={col.colClass} />
            ))}
          </colgroup>
          <thead className="bg-gray-50">
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col.label} className={col.thClass}>
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {leads.map((lead, index) => (
              <tr key={`${lead.id}-${index}`} className="hover:bg-gray-50 transition-colors">
                <td className={TABLE_COLUMNS[0].tdClass}>
                  <span className="font-medium text-gray-600 tabular-nums">
                    {serialOffset + index + 1}
                  </span>
                </td>
                <td className={TABLE_COLUMNS[1].tdClass}>
                  <div className="font-medium text-gray-900">{lead.name || 'N/A'}</div>
                </td>
                <td className={TABLE_COLUMNS[2].tdClass}>{lead.phone || 'N/A'}</td>
                <td className={TABLE_COLUMNS[3].tdClass}>{displayValue(lead.email)}</td>
                <td className={TABLE_COLUMNS[4].tdClass}>{displayValue(lead.company_name)}</td>
                <td className={TABLE_COLUMNS[5].tdClass}>{displayValue(lead.address)}</td>
                <td className={TABLE_COLUMNS[6].tdClass}>{displayValue(lead.vehicle_brand_name)}</td>
                <td className={TABLE_COLUMNS[7].tdClass}>{displayValue(lead.dnd_status)}</td>
                <td className={TABLE_COLUMNS[8].tdClass}>{displayValue(lead.plan)}</td>
                <td className={TABLE_COLUMNS[9].tdClass}>{lead.age ?? 'N/A'}</td>
                <td className={TABLE_COLUMNS[10].tdClass}>{lead.city || 'N/A'}</td>
                <td className={TABLE_COLUMNS[11].tdClass}>{formatCurrency(lead.income)}</td>
                <td className={TABLE_COLUMNS[12].tdClass}>
                  <span className="inline-flex max-w-full px-2 py-1 text-xs font-medium rounded-full bg-primary-100 text-primary-800 whitespace-normal break-words">
                    {lead.source_name || 'N/A'}
                  </span>
                </td>
                <td className={TABLE_COLUMNS[13].tdClass}>{lead.campaign_name || 'N/A'}</td>
                <td className={TABLE_COLUMNS[14].tdClass}>
                  <span className="inline-flex max-w-full px-2 py-1 text-xs font-medium rounded-full bg-secondary-100 text-secondary-800 capitalize whitespace-nowrap">
                    {displayValue(lead.data_type)}
                  </span>
                </td>
                <td className={TABLE_COLUMNS[15].tdClass}>{formatDate(lead.created_at)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        
        {leads.length === 0 && (
          <div className="text-center py-12">
            <Eye size={48} className="mx-auto text-gray-300 mb-4" />
            <p className="text-gray-500">No leads found matching your criteria.</p>
          </div>
        )}
      </div>
    </>
  );

  if (embedded) {
    return tableBody;
  }

  return <Panel className="overflow-hidden">{tableBody}</Panel>;
};

export default LeadTable;
